<script setup>
// Profile card — a prominent top photo, name, role/title, optional LinkedIn
// link, and a bio that shows its opening lines with a "Show more" expander.
// The bio may be a single line (team members) or a long multi-paragraph story
// (Academy alumni) — blank-line-separated paragraphs render as separate <p>.
// Photo is optional: without one, a monogram tile stands in. Used for the
// /about/our-people team grid and the Academy's "Graduate stories" (via
// ProfileCardsBlock). Replaces the old AcademyStoryCard.
import { ref, computed, onMounted, nextTick } from 'vue'

const props = defineProps({
  name:       { type: String, required: true },
  pronouns:   { type: String, default: '' },     // e.g. 'she/her' — shown directly under the name, above the title
  title:      { type: String, default: '' },     // role / job title — optional
  photo:      { type: String, default: '' },     // headshot URL — falls back to a monogram tile
  linkedin:   { type: String, default: '' },     // full profile URL — optional
  quote:      { type: String, default: '' },     // lead pull-quote, shown prominently above the bio — optional
  bio:        { type: String, default: '' },
  photoAspect:{ type: String, default: '5 / 3' }, // photo box ratio — '1 / 1' for square headshots (no crop)
  photoPosition:{ type: String, default: 'center' } // object-position override — e.g. 'center top' keeps tall/square crops headroom
})

// Split the bio on blank lines so long stories keep their paragraph breaks;
// a single-line bio just yields one paragraph.
const paragraphs = computed(() =>
  props.bio.split(/\n{2,}/).map(p => p.trim()).filter(Boolean)
)

// First letter of the name for the photo-less monogram fallback.
const initial = computed(() => (props.name.trim()[0] || '').toUpperCase())

const expanded   = ref(false)
const needsToggle = ref(false)
const bioEl      = ref(null)
const cardEl     = ref(null)

// Only surface the toggle when the bio is actually clamped (overflows its
// 3-line box). Runs client-side after hydration; the SSG output ships the
// collapsed state, so short bios simply render in full with no button.
onMounted(async () => {
  await nextTick()
  const el = bioEl.value
  if (el) needsToggle.value = el.scrollHeight > el.clientHeight + 1
})

// Toggle the bio, then re-anchor the card in view. Inline expansion grows the
// card (and, via the equal-height grid, its whole row) in place, so without
// this the clicked card slides up or down under the pointer. `block: 'nearest'`
// keeps a fully-visible card put and only pulls one back when its top has
// scrolled off, or its revealed bio now sits below the fold.
async function toggle () {
  expanded.value = !expanded.value
  await nextTick()
  cardEl.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}
</script>

<template>
  <div ref="cardEl" class="card profile-card" :class="{ interactive: needsToggle }" @click="needsToggle && toggle()">
    <img v-if="photo" :src="photo" :alt="name" class="photo" :style="{ aspectRatio: photoAspect, objectPosition: photoPosition }" />
    <div v-else class="photo placeholder" aria-hidden="true" :style="{ aspectRatio: photoAspect }">
      <span>{{ initial }}</span>
    </div>

    <div class="card-body">
      <div class="identity">
        <div class="head">
          <h3>{{ name }}</h3>
          <a
            v-if="linkedin"
            class="linkedin"
            :href="linkedin"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="`${name} on LinkedIn`"
            @click.stop
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
              <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
            </svg>
          </a>
        </div>

        <p v-if="pronouns" class="pronouns">{{ pronouns }}</p>
        <p v-if="title" class="title">{{ title }}</p>
      </div>

      <blockquote v-if="quote" class="lead-quote">{{ quote }}</blockquote>

      <div v-if="paragraphs.length" class="bio-wrap">
        <div ref="bioEl" class="bio" :class="{ clamped: !expanded }">
          <p v-for="(para, i) in paragraphs" :key="i">{{ para }}</p>
        </div>
        <button
          v-if="needsToggle"
          type="button"
          class="read-more"
          :aria-expanded="expanded"
          @click.stop="toggle()"
        >
          {{ expanded ? 'Show less −' : 'Show more +' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-card {
  display: flex;
  flex-direction: column;
  box-shadow: none;             // drop the global .card rest shadow — OurWork has none at rest
  scroll-margin-top: $space-6;  // breathing room when toggle() re-anchors the card in view

  // Whole-card hover, mirroring OurWorkCard/.card.interactive: lift + warm-beige
  // → white + soft shadow. Applied only when the bio is expandable (`interactive`
  // class set from `needsToggle`), so the affordance appears only where clicking
  // the card actually does something (toggles Show more/less).
  &.interactive {
    cursor: pointer;
    @include transition(box-shadow, background, transform);

    &:hover {
      background: var(--color-surface);
      box-shadow: 0 4px 6px rgba($color-ink, 0.16);
      transform: translateY(-2px);
    }
    &:active { transform: translateY(1px); }
  }

  .photo {
    display: block;
    width: 100%;
    // aspect-ratio and object-position are set inline from the `photoAspect`
    // (default 5/3; '1 / 1' for square headshots) and `photoPosition`
    // (default 'center') props.
    object-fit: cover;
  }

  // Photo-less fallback — a soft branded tile with the person's initial, kept
  // at the same 5:3 footprint so cards line up whether or not a headshot exists.
  .photo.placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-card-cream);
    color: var(--color-primary);

    span {
      font-size: $font-size-h1;
      font-weight: 700;
      line-height: 1;
    }
  }

  .card-body {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    flex: 1;
  }

  // Name, pronouns and title read as one tight identity cluster — a small gap
  // holds them together, separate from the wider card-body rhythm below.
  .identity {
    display: flex;
    flex-direction: column;
    gap: $space-1;
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-3;

    h3 {
      margin: 0;   // strip the UA 1em margins — in a flex column they'd add to the gap
      font-size: $font-size-h4;
      line-height: $lh-heading-sm;
      color: var(--color-ink);
    }

    .linkedin {
      display: inline-flex;
      flex-shrink: 0;
      color: var(--color-primary);
      @include transition(color);

      &:hover { color: var(--color-primary-hover); }
    }
  }

  .pronouns {
    margin: 0;   // strip UA <p> margins so the .identity gap controls spacing
    font-size: $font-size-cap;
    line-height: $lh-heading-sm;
    color: var(--color-text-muted);
  }

  .title {
    margin: 0;   // strip UA <p> margins so the .identity gap controls spacing
    font-size: $font-size-sm;
    line-height: $lh-heading-sm;
    font-weight: 500;
    color: var(--color-text-muted);
  }

  // Lead pull-quote — larger, medium-weight, with a left accent rule so the
  // graduate's own words read as a prominent quote above the story prose
  // (mirrors QuoteBlock, sized down to sit inside a card).
  .lead-quote {
    margin: $space-2 0 0;
    padding-left: $space-4;
    border-left: 3px solid var(--color-quote-accent);
    font-size: $font-size-base;   // one step down from md, sitewide quote-size review
    line-height: $lh-heading-sm;
    letter-spacing: $ls-heading;
    font-weight: 500;
    color: var(--color-ink);
  }

  // Fills the remaining card height so the toggle can pin to the bottom (below).
  // In the equal-height grid this keeps every card's "Show more" on the same
  // baseline — cards align at the bottom, and the row's slack reads as an
  // intentional footer gap rather than dead space trailing under the button.
  .bio-wrap {
    margin-top: $space-4;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: $space-3;   // minimum space between the bio and the toggle
  }

  .bio {
    color: var(--color-text);
    line-height: $lh-base;

    p { margin: 0; }
    p + p { margin-top: $space-3; }

    &.clamped {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  // Uses the site's `.cta` underline signature (OurWorkCard / NewsCard /
  // ResourceCard): sm, weight 600, primary, with an animated grow-from-left
  // underline (a `background-image` line whose `background-size` animates from
  // 0% → 100%) rather than a static text-decoration. The +/− symbol is inline
  // in the label so the line runs continuously beneath it. The reveal is
  // triggered on whole-card hover (like the OurWork CTA) and on the button's
  // own hover/focus. `background-color: transparent` (not `background: none`)
  // keeps the button chrome-free without wiping the underline gradient.
  .read-more {
    margin-top: auto;   // pin to the card bottom; `.bio-wrap` gap keeps the min space above
    align-self: flex-start;
    padding: 0 0 2px;
    border: none;
    cursor: pointer;
    font-size: $font-size-sm;
    font-weight: 600;
    color: var(--color-primary);
    background-color: transparent;
    background-image: linear-gradient(currentColor, currentColor);
    background-repeat: no-repeat;
    background-position: 0 100%;
    background-size: 0% 1px;
    @include transition(background-size, color);

    &:hover         { color: var(--color-primary-hover); }
    &:focus-visible { @include focus-ring; }
    &:hover, &:focus-visible { background-size: 100% 1px; }
  }

  // Whole-card hover also reveals the toggle underline, mirroring the OurWork
  // card where the CTA underline draws in on card hover.
  &.interactive:hover .read-more { background-size: 100% 1px; }
}
</style>
